/**
 * Hooks React Query pour les projets.
 *
 * Avant, Projects.tsx et ProjectDetail.tsx chargeaient via useEffect. Sur le
 * serveur il n'y a pas d'effet : ces pages sortaient donc vides du pre-rendu.
 * En passant par React Query, le cache peut etre seme au build et le HTML
 * contient les projets.
 */

import { useQuery } from '@tanstack/react-query';
import { fetchAllPages } from '@/lib/api';

const API_URL = import.meta.env.DEV
  ? // En developpement, on passe par le proxy Vite : le backend n'autorise en
    // CORS que localhost:8080, localhost:5173 et la production, donc un appel
    // direct depuis n'importe quel autre port est bloque par le navigateur.
    "/api"
  : "https://profile-sparkle-magic-production.up.railway.app/api";

export interface Project {
  id: number;
  title: string;
  slug: string;
  short_description: string;
  project_type: string;
  project_type_display: string;
  cover_image: string;
  technologies: string[];
  live_url: string;
  github_url: string;
  featured: boolean;
  order: number;
}

export interface Technology {
  id: number;
  name: string;
  slug: string;
  icon: string;
  color: string;
}

export interface ProjectDetail extends Omit<Project, 'technologies'> {
  description: string;
  status: string;
  screenshots: string[];
  technologies: Technology[];
  demo_video_url: string;
  started_at: string | null;
  completed_at: string | null;
  created_at: string;
  updated_at: string;
}

export function useProjects() {
  const { data, isPending, error } = useQuery({
    queryKey: ['projects'],
    queryFn: () => fetchAllPages<Project>(`${API_URL}/projects/`),
    staleTime: 5 * 60 * 1000,
  });

  return {
    projects: data ?? [],
    loading: isPending,
    error: error ? (error as Error).message : null,
  };
}

export function useProject(slug: string) {
  const { data, isPending, error } = useQuery({
    queryKey: ['project', slug],
    queryFn: async (): Promise<ProjectDetail | null> => {
      const response = await fetch(`${API_URL}/projects/${slug}/`);
      if (response.status === 404) return null;
      if (!response.ok) {
        throw new Error('Le projet est momentanément indisponible. Réessayez plus tard.');
      }
      return response.json();
    },
    enabled: !!slug,
    staleTime: 5 * 60 * 1000,
    retry: false,
  });

  return {
    project: data ?? null,
    loading: isPending,
    error: error ? (error as Error).message : null,
  };
}
